import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api'
import { BEREICHE, bereichVon } from '../bereiche'
import { zeitpunkt } from '../format'
import { useAktion, useLaden } from '../hooks'
import { laufPfad, notierungLaufPfad } from '../pfade'
import { IMPORTSTATUS, MAILSTATUS, NOTIERUNG_LAUFSTATUS } from '../texte'
import type { BereichId, MailEingang } from '../typen'
import Meldungen from './Meldungen'

const ARTTEXT: Record<string, string> = { pdf: 'PDF', excel: 'Excel', sonstiges: 'nicht gelesen' }

interface Eigenschaften {
  /** nur die Mails dieses Bereichs; ohne: alle, mit Spalte „Bereich" */
  bereich?: BereichId
  /** „Erlauben" an einer abgewiesenen Mail schaltet den Absender frei - die Seite lädt dann ihre Liste neu */
  onAbsenderErlaubt?: () => void
}

/**
 * Was zuletzt in den Postfächern ankam: Absender, Betreff, Anhänge, in welchen Bereich die Mail
 * sortiert wurde und warum, und welche Läufe daraus entstanden sind. Eine Mail, die weder Regeln noch
 * KI zuordnen konnten (Status „Bereich unklar"), bekommt je Bereich einen Knopf - ein Klick ordnet zu
 * und verarbeitet sie dort neu.
 *
 * Steht auf den Einstellungen der Verpackung (alle Mails) und unter Importe · Eingänge der
 * Rohstoffnotierungen (nur deren Mails).
 */
export default function MailEingaenge({ bereich, onAbsenderErlaubt }: Eigenschaften) {
  // Welcher Knopf gerade läuft („erneut-<id>", „bereich-<id>") - nur der zeigt es an, die anderen sind gesperrt.
  const [laeuft, setLaeuft] = useState<string | null>(null)
  const [offen, setOffen] = useState<number | null>(null)
  const { fehler, erfolg, setErfolg, ausfuehren } = useAktion()
  const { daten, fehler: ladeFehler, setDaten, neuLaden } = useLaden(() => api.postfachEingaenge(50, bereich), [bereich])
  const eingaenge = daten ?? []
  const mitBereich = bereich === undefined

  function ersetzen(neu: MailEingang) {
    setDaten((alt) => (alt ?? []).map((e) => (e.id === neu.id ? neu : e)))
  }

  function erneut(eingang: MailEingang) {
    setLaeuft(`erneut-${eingang.id}`)
    void ausfuehren(async () => {
      const neu = await api.postfachErneut(eingang.id)
      const anzahl = neu.laeufe.length + (neu.notierung_laeufe?.length ?? 0)
      setErfolg(anzahl > 0 ? `Verarbeitet - ${anzahl} Lauf/Läufe unter Importe.` : `Verarbeitet, aber ${neu.hinweis ?? 'nichts daraus geworden'}.`)
      ersetzen(neu)
    }).finally(() => setLaeuft(null))
  }

  function zuordnen(eingang: MailEingang, ziel: BereichId) {
    setLaeuft(`bereich-${eingang.id}`)
    void ausfuehren(async () => {
      const neu = await api.postfachBereich(eingang.id, ziel)
      setErfolg(`Die Mail gehört jetzt zu ${bereichVon(ziel).titel} und wurde dort neu verarbeitet.`)
      // In der gefilterten Liste verschwindet eine Mail, die in den anderen Bereich ging.
      if (bereich && neu.bereich !== bereich) neuLaden()
      else ersetzen(neu)
    }).finally(() => setLaeuft(null))
  }

  function erlauben(eingang: MailEingang) {
    void ausfuehren(async () => {
      const neu = await api.postfachAbsenderAnlegen(eingang.adresse, null)
      setErfolg(`${neu.adresse} darf jetzt schicken. „Nachholen“ wertet die Mail aus.`)
      onAbsenderErlaubt?.()
      neuLaden()
    })
  }

  const spalten = mitBereich ? 7 : 6

  return (
    <>
      <Meldungen fehler={ladeFehler ?? fehler} erfolg={erfolg} />
      <div className="tabellenrahmen">
        <table className={`mail-tabelle${mitBereich ? ' mit-bereich' : ''}`}>
          <thead>
            <tr>
              <th>Empfangen</th>
              <th>Absender</th>
              <th>Betreff</th>
              <th>Inhalt</th>
              {mitBereich && <th>Bereich</th>}
              <th>Ergebnis</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {daten !== null && eingaenge.length === 0 && (
              <tr>
                <td colSpan={spalten} className="leer">
                  {bereich === 'notierungen'
                    ? 'Noch keine Notierung per Mail angekommen. Lieferanten schicken an othuena@intsode.com oder othuena@vai.com.de; erkannt wird am Absender (Stammdaten · Lieferanten).'
                    : 'Noch keine Mail abgeholt.'}
                </td>
              </tr>
            )}
            {eingaenge.map((e) => {
              const mailstatus = e.status ? MAILSTATUS[e.status] : undefined
              return (
                <tr key={e.id} className={e.status === 'abgewiesen' ? 'leise' : undefined}>
                  <td title={e.abgeholt_am ? `abgeholt ${zeitpunkt(e.abgeholt_am)}` : undefined}>
                    {zeitpunkt(e.empfangen_am ?? e.abgeholt_am)}
                  </td>
                  <td title={e.pruefung ? `Prüfung des Mailservers: ${e.pruefung}` : 'ohne Prüfergebnis des Mailservers'}>
                    {e.absender}
                    {e.postfach && <div className="hinweis">an {e.postfach}</div>}
                  </td>
                  <td>{e.betreff ?? <span className="hinweis">ohne Betreff</span>}</td>
                  <td>
                    {e.anhaenge.map((a) => (
                      <div key={a.name}>
                        {a.name} <span className="hinweis">({ARTTEXT[a.art] ?? a.art})</span>
                      </div>
                    ))}
                    {e.text && (
                      <div>
                        <button className="leise" onClick={() => setOffen(offen === e.id ? null : e.id)}>
                          {offen === e.id ? 'Text ausblenden' : 'Text'}
                        </button>
                        {offen === e.id && <pre className="mail-entwurf mail-text">{e.text}</pre>}
                      </div>
                    )}
                    {e.anhaenge.length === 0 && !e.text && <span className="hinweis">leer</span>}
                  </td>
                  {mitBereich && (
                    <td>
                      {e.bereich ? <b title={bereichVon(e.bereich).titel}>{bereichVon(e.bereich).kurztitel}</b> : e.status === 'zuordnen' ? <b>unklar</b> : <span className="hinweis">-</span>}
                      {e.bereich_grund && <div className="hinweis">{e.bereich_grund}</div>}
                    </td>
                  )}
                  <td>
                    <span className={`status ${mailstatus?.klasse ?? ''}`}>{mailstatus?.text ?? e.status ?? 'vor der Umstellung'}</span>
                    {e.hinweis && <div className="hinweis">{e.hinweis}</div>}
                    {!mitBereich && e.bereich_grund && <div className="hinweis">{e.bereich_grund}</div>}
                    {e.laeufe.map((l) => (
                      <div key={`v${l.id}`}>
                        <Link className="hinweis-link" to={laufPfad(l)}>
                          Import {l.id}: {l.dateiname ?? l.dokumenttyp}
                        </Link>{' '}
                        <span className={`status ${IMPORTSTATUS[l.status].klasse}`}>{IMPORTSTATUS[l.status].text}</span>
                      </div>
                    ))}
                    {(e.notierung_laeufe ?? []).map((l) => (
                      <div key={`n${l.id}`}>
                        <Link className="hinweis-link" to={notierungLaufPfad(l.id)}>
                          Notierungslauf {l.id}
                        </Link>{' '}
                        <span className={`status ${NOTIERUNG_LAUFSTATUS[l.status].klasse}`}>{NOTIERUNG_LAUFSTATUS[l.status].text}</span>
                      </div>
                    ))}
                  </td>
                  <td>
                    {e.status === 'zuordnen' && (
                      <div className="knopf-stapel">
                        {BEREICHE.map((b) => (
                          <button key={b.id} disabled={laeuft !== null} title={`Die Mail gehört zu ${b.titel}`} onClick={() => zuordnen(e, b.id)}>
                            {laeuft === `bereich-${e.id}` ? 'Läuft…' : `zu ${b.kurztitel}`}
                          </button>
                        ))}
                      </div>
                    )}
                    {e.freischaltbar && (
                      <button className="leise" onClick={() => erlauben(e)}>
                        Erlauben
                      </button>
                    )}{' '}
                    {e.erneut_moeglich && e.status !== 'zuordnen' && (
                      <button disabled={laeuft !== null} onClick={() => erneut(e)}>
                        {laeuft === `erneut-${e.id}` ? 'Läuft…' : 'Nachholen'}
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}
